import type { AiManagedObjectSourceApp } from '@atlassian/ai-managed-objects/types';

import { getSourceAppFromUrl } from './configuration';
import type { ManagedBlockSourceInvalidReason } from './types';

export type SmartLinkResponseLike = {
	data?: unknown;
	meta?: unknown;
};

const APP_MATCHERS: Array<[AiManagedObjectSourceApp, RegExp]> = [
	['BITBUCKET', /bitbucket/],
	['CONFLUENCE', /confluence/],
	['GITHUB', /github/],
	['GOOGLE_DRIVE', /google|gdrive|drive|docs/],
	['JIRA', /jira/],
	['LOOM', /loom/],
	['MICROSOFT_SHAREPOINT', /sharepoint|onedrive/],
	['MICROSOFT_TEAMS', /microsoft.?teams|ms.?teams/],
	['SALESFORCE', /salesforce/],
	['SLACK', /slack/],
];

const getTextValue = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const getRecord = (value: unknown): Record<string, unknown> | undefined =>
	value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

export const getResolvedApp = (
	url: string,
	response: SmartLinkResponseLike,
): AiManagedObjectSourceApp => {
	const meta = getRecord(response.meta);
	const data = getRecord(response.data);
	const metaGenerator = getRecord(meta?.generator);
	const dataGenerator = getRecord(data?.generator);
	const candidates = [
		meta?.product,
		meta?.key,
		meta?.definitionId,
		metaGenerator?.name,
		metaGenerator?.['@id'],
		dataGenerator?.name,
		dataGenerator?.['@id'],
		data?.url,
		url,
	]
		.map(getTextValue)
		.filter((candidate): candidate is string => Boolean(candidate))
		.map((candidate) => candidate.toLowerCase());

	return (
		APP_MATCHERS.find(([, matcher]) =>
			candidates.some((candidate) => matcher.test(candidate)),
		)?.[0] ?? getSourceAppFromUrl(url)
	);
};

export const getInvalidReasonFromResponse = (
	response: SmartLinkResponseLike,
): ManagedBlockSourceInvalidReason | null => {
	const meta = getRecord(response.meta);
	const access = getTextValue(meta?.access)?.toLowerCase();
	const visibility = getTextValue(meta?.visibility)?.toLowerCase();

	if (visibility === 'not_found') {
		return 'not-found';
	}

	if (access === 'unauthorized' || access === 'forbidden') {
		return 'unauthorized';
	}

	if (access === 'unauthenticated') {
		return 'unauthenticated';
	}

	if (access && access !== 'granted') {
		return 'unavailable';
	}

	return null;
};

export const getInvalidReasonFromError = (error: unknown): ManagedBlockSourceInvalidReason => {
	const errorText = error instanceof Error ? `${error.name} ${error.message}`.toLowerCase() : '';

	if (errorText.includes('auth')) {
		return 'unauthenticated';
	}

	if (errorText.includes('forbidden') || errorText.includes('permission')) {
		return 'unauthorized';
	}

	if (errorText.includes('unsupported') || errorText.includes('not found')) {
		return 'not-found';
	}

	return 'unavailable';
};
